import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { ReceivingLogComponent } from './components/receiving/receiving-log/receiving-log.component';
import { LogFormComponent } from './components/receiving/log-form/log-form.component';

import { LaydownLogFormComponent } from './components/laydown-space/laydown-new-log/laydown-log-form.component';
import { LaydownLogTableComponent } from './components/laydown-space/laydown-log-table/laydown-log-table.component';
import { SofReceivingPageComponent } from './components/sof-receiving/sof-receiving-page/sof-receiving-page.component';
import { ViewLoggedItemComponent } from './components/receiving/view-logged-item/view-logged-item.component';
import { InstallHelpComponent } from './components/install-help/install-help.component';

import { MsalGuard } from '@azure/msal-angular'; // Import the MsalGuard
import { RoleGuard } from './guards/role.guard';// Import the RoleGuard

const routes: Routes = [

  { path: 'receiving/view-logs', component: ReceivingLogComponent, canActivate: [MsalGuard, RoleGuard] },

  // The outgoing desktop form. `new-log` itself now belongs to the shared flow
  // below — this one keeps a route of its own (rather than none) so a bookmark or
  // an in-flight tab still resolves while desktop is being moved over, and so the
  // rollout flag has somewhere to send desktop while it is off.
  { path: 'receiving/new-log-classic', component: LogFormComponent, canActivate: [MsalGuard, RoleGuard] },

  // NOTE: there is deliberately no `receiving/edit-log/:logId` route. The
  // handoff-18 row editor is a modal NESTED in the log table's Entry Details
  // modal (ReceivingLogEditComponent), not a page. It commits on its own, and the
  // parent modal has to re-baseline from what it saved or the next "Update Entry"
  // pushes the pre-edit `quantity`/`items` strings back over the derived ones. A
  // deep link has no parent to do that, so it would reintroduce exactly the
  // contradiction the feature exists to remove.

    { path: 'laydown/view-logs', component: LaydownLogTableComponent, canActivate: [MsalGuard, RoleGuard] },
  { path: 'laydown/new-log', component: LaydownLogFormComponent, canActivate: [MsalGuard, RoleGuard] },

  { path: 'sof-receiving', component: SofReceivingPageComponent, canActivate: [MsalGuard, RoleGuard] },
  { path: 'sof-receiving/history', component: SofReceivingPageComponent, canActivate: [MsalGuard, RoleGuard], data: { readOnly: true } },

  // The page an item's QR code resolves to (?payload={itemGuid}); scanning with
  // a phone camera lands here behind the normal auth guards.
  { path: 'view-logged-item', component: ViewLoggedItemComponent, canActivate: [MsalGuard, RoleGuard] },

  // The shared receiving flow (mobile, and desktop once the rollout flag is on).
  // Its steps are children: /receiving/new-log/scan, /details, /items, …
  {
    path: 'receiving/new-log',
    canActivate: [MsalGuard, RoleGuard],
    loadChildren: () => import('./components/receiving/receiving-mobile/receiving-mobile.module').then(m => m.ReceivingMobileModule),
  },

  // Public PWA install instructions. Deliberately UNGUARDED — no MsalGuard, no
  // RoleGuard: the people who need this page are the ones who cannot get past a
  // login. Someone installing the app for the first time has no session yet, and
  // a desktop user reading the steps may not be signed into that browser at all.
  // The page reads no API and shows nothing user-specific, so there is nothing
  // for a guard to protect; guarding it would only make the link useless.
  { path: 'install-help', component: InstallHelpComponent },

  { path: '', component: LandingPageComponent },
  // Wildcard route for unknown paths
  { path: '**', redirectTo: ''  },
];

/**
 * AppRoutingModule handles the application's routing configuration.
 *  Both of the main routes are protected by the MsalGuard and RoleGuard. The MsalGuard ensures that the user is authenticated, 
 * and the RoleGuard ensures that the user has the correct role to access the route.
 * - '/receiving/view-logs' - The ReceivingLogComponent displays the receiving log.
 * - '/receiving/new-log' - The shared receiving flow (lazy-loaded), which owns its
 *   own child routes for each step.
 * - '/receiving/new-log-classic' - The LogFormComponent, the outgoing desktop form.
 * - '/install-help' - The InstallHelpComponent shows the PWA install steps. This is
 *   the one route with no guards at all, since it has to be readable before login.
 * - '/' - The LandingPageComponent displays the landing page.
 * - '* - Supports a wildcard route to redirect unknown paths to the landing page.
 */

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
